import jsPDF from "jspdf";
import { getReportDefinition } from "./reportRegistry";
import { drawHeaderEmpresa } from "../plantillas/encabezados/headerEmpresa";
import { drawPacienteInfo } from "../plantillas/paciente/pacienteInfo";
import { drawFirmas, applyDocumentFooters } from "../plantillas/piePagina/footerFirmas";
import { PDF_LAYOUT } from "../utils/pdfStyles";

export const getDownloadFileName = (plan) => {
  const p = plan?.plan || plan || {};
  const tipo = (p?.paciente_tipo_id || p?.Tipo_id || "CC").toString().trim().replace(/[^a-zA-Z0-9]/g, "");
  const num = (p?.paciente_identificacion || p?.Identificacion || p?.Unic_ID || "").toString().trim().replace(/[^a-zA-Z0-9]/g, "");
  
  const rawNombre = (p?.paciente_nombre || p?.fullname || "paciente").toString().trim();
  const nombreLimpio = rawNombre
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quitar acentos
    .toLowerCase()
    .replace(/\s+/g, "") // sin espacios, ej. nombreapellido1
    .replace(/[^a-z0-9_-]/g, "");

  return `${tipo}${num}-${nombreLimpio}.pdf`;
};

const getSedeActivaMeta = () => {
  try {
    const raw = sessionStorage.getItem("sedeActiva");
    if (!raw || raw === "undefined" || raw === "null") return {};
    const s = JSON.parse(raw);
    return {
      empresa: s?.nombreEmpresa || s?.empresa || "",
      nit: s?.nit || "",
      sede: s?.nombreSede || s?.nombre || "",
      codigoSede: s?.codigoSede || "",
      logo: s?.logoEmpresa || s?.logo || null,
    };
  } catch (e) {
    console.warn("[reportEngine] No se pudo leer sedeActiva:", e);
    return {};
  }
};

/**
 * Función central e invocador universal de reportes PDF.
 * Ensambla: Encabezado Institucional -> Ficha Paciente -> Reporte Específico -> Firmas -> Pie de Página
 *
 * @param {Object} params
 * @param {string} params.reportName Identificador del reporte (ej: 'planParto')
 * @param {Object} params.data Datos crudos devueltos por el backend ({ plan, eps_tramite, etc. })
 * @param {Object} params.meta Metadatos opcionales (logo, fechas, empresa, etc.)
 * @returns {Blob} Blob del documento PDF generado
 */
export const generarReportePdf = ({ reportName, data, meta = {} }) => {
  const reportDef = getReportDefinition(reportName);
  if (!reportDef) {
    throw new Error(`No se encontró una plantilla registrada para el reporte "${reportName}"`);
  }

  const payload = data || {};
  const plan = payload.plan || payload;
  const epsTramite = payload.eps_tramite || {};

  // Merge institutional metadata: prioritize database plan fields, then meta, then session
  const sessionMeta = getSedeActivaMeta();
  const logo = plan?.empresa_logo || plan?.logo || meta?.logo || sessionMeta.logo || null;

  const mergedMeta = {
    ...sessionMeta,
    empresa: plan?.empresa_nombre || meta?.empresa || sessionMeta.empresa,
    nit: plan?.empresa_nit || meta?.nit || sessionMeta.nit,
    sede: plan?.sede_nombre || meta?.sede || sessionMeta.sede,
    codigoSede: plan?.sede_codigo || meta?.codigoSede || sessionMeta.codigoSede,
    ...meta,
    logo,
  };

  // Instanciar documento jsPDF A4
  const doc = new jsPDF({
    unit: PDF_LAYOUT.unit,
    format: PDF_LAYOUT.format,
  });

  const folio = plan?.id_plan_parto || meta?.folio || null;

  // 1. Encabezado institucional con Logo y Empresa
  let currentY = drawHeaderEmpresa(doc, {
    meta: mergedMeta,
    documentTitle: reportDef.documentTitle,
    folio,
    startY: PDF_LAYOUT.marginTop,
  });

  // 2. Ficha clínica y datos del paciente
  currentY = drawPacienteInfo(doc, {
    plan,
    startY: currentY,
  });

  // 3. Contenido específico del reporte
  if (typeof reportDef.render === "function") {
    currentY = reportDef.render(doc, {
      plan,
      epsTramite,
      startY: currentY,
    });
  }

  // 4. Bloque médico-legal de firmas
  drawFirmas(doc, {
    plan,
    startY: currentY,
  });

  // 5. Aplicar pie de página y paginación en todas las hojas
  applyDocumentFooters(doc, {
    legalText: reportDef.legalText,
    folio,
    plan,
  });

  const filename = getDownloadFileName(plan);
  doc.setDocumentProperties({
    title: filename,
    subject: reportDef.documentTitle || "Reporte Clínico",
    author: mergedMeta.empresa || "APIM",
  });

  const blob = doc.output("blob");
  blob.filename = filename;
  return blob;
};
